/**
 * CicdPipeline: admin page at /cicd-pipeline, shown in the UI as "CI/CD Monitor".
 *
 * The name is deliberately CI/CD Monitor and not Pipeline Monitor: the webapp already
 * has a pipeline view under /pipeline for the SAP Pipeline Concept, and two pages with
 * "Pipeline" in the name pointed at completely different things. The route stays
 * /cicd-pipeline so existing links and bookmarks keep working.
 *
 * Order (top to bottom):
 *   1. Health bar (GitHub connected, repo private, workflow active, stages ready)
 *   2. Stage flow diagram DEV >> QAS >> PROD (incl. repo label and "unbind" action)
 *   3. Automations in the repository (active workflows plus SAP templates, once per space switch)
 *   4. Release candidate plus approval footer
 *   5. Currently in the pipeline (active runs)
 *   6. Runs (history with an expandable step timeline)
 *
 * Auto refresh: 5s while a run is active, otherwise 25s.
 * Motion: 150-300ms, transform/opacity only, disabled via prefers-reduced-motion.
 * Colors: only --iio-* tokens, tints via color-mix(in srgb, var(--iio-*) X%, transparent).
 * All cp-* classes are scoped under .cp-root.
 */
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import { STAGES, stageLabel } from "../utils/stages";

const CSS = `
.cp-root{ color:var(--iio-text-1); }
.cp-chev{ color:var(--iio-text-3); font-weight:800; letter-spacing:-1px; }

.cp-ph{ margin-bottom:var(--iio-s-6); }
.cp-ph .crumb{ font-size:11px; letter-spacing:.04em; color:var(--iio-text-3); margin-bottom:7px; }
.cp-ph .crumb b{ color:var(--iio-signal); font-weight:700; }
.cp-ph h1{ margin:0; font-size:27px; font-weight:700; letter-spacing:-.02em; }
.cp-ph .sub{ margin-top:6px; font-size:13.5px; color:var(--iio-text-2); max-width:760px; line-height:1.5; }

.cp-bar{ display:flex; align-items:center; gap:12px; margin:16px 0 var(--iio-s-5); flex-wrap:wrap; }
.cp-sel{ height:36px; padding:0 30px 0 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); font-size:13.5px; color:var(--iio-text-1); appearance:none; font-family:inherit; outline:none; min-width:220px; cursor:pointer; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237A8590' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; transition:border-color var(--iio-tx-fast); }
.cp-sel:focus{ border-color:var(--iio-brand-ink); }
.cp-refresh{ display:inline-flex; align-items:center; gap:7px; height:36px; padding:0 14px; border:1px solid var(--iio-border); border-radius:var(--iio-r-md); background:transparent; color:var(--iio-text-2); font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:color var(--iio-tx-fast), background var(--iio-tx-fast), border-color var(--iio-tx-fast), transform var(--iio-tx-fast); }
.cp-refresh:hover:not(:disabled){ background:color-mix(in srgb, var(--iio-brand-ink) 5%, transparent); color:var(--iio-text-1); border-color:color-mix(in srgb, var(--iio-brand-ink) 45%, var(--iio-border)); }
.cp-refresh:active:not(:disabled){ transform:translateY(1px); }
.cp-refresh:disabled{ opacity:.5; cursor:not-allowed; }
.cp-refresh svg{ width:15px; height:15px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; transition:transform var(--iio-tx-med); }
.cp-refresh:hover:not(:disabled) svg{ transform:rotate(-45deg); }
.cp-live{ display:inline-flex; align-items:center; gap:7px; font-size:12px; color:var(--iio-text-3); margin-left:auto; }
.cp-live .beat{ width:8px; height:8px; border-radius:50%; background:var(--iio-text-4); }
.cp-live.on .beat{ background:var(--iio-info); animation:cpPulse 1.4s ease-in-out infinite; }

/* Health bar */
/* PAT banner: names the cause of the scattered 401 errors ABOVE all cards. Regular
   card frame, only the title carries the alert color. */
.cp-pat-banner{ display:flex; flex-direction:column; gap:10px; padding:14px 16px; margin-bottom:var(--iio-s-5);
  border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-2); }
.cp-pat-banner .ttl{ font-size:14px; font-weight:700; color:var(--iio-critical); }
.cp-pat-banner .txt{ font-size:13px; color:var(--iio-text-2); line-height:1.55; max-width:86ch; }
.cp-pat-banner .row{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.cp-pat-banner .msg{ font-size:12.5px; }
.cp-pat-banner .msg.ok{ color:var(--iio-success); }
.cp-pat-banner .msg.bad{ color:var(--iio-critical); }

.cp-health{ display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:12px; margin-bottom:var(--iio-s-5); }
.cp-hcard{ display:flex; align-items:center; gap:12px; padding:13px 15px; border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-2); transition:transform var(--iio-tx-med), border-color var(--iio-tx-med), box-shadow var(--iio-tx-med); }
.cp-hcard:hover{ border-color:color-mix(in srgb, var(--iio-brand-ink) 30%, var(--iio-border)); transform:translateY(-1px); box-shadow:var(--iio-shadow-sm); }
.cp-hcard .ic{ width:34px; height:34px; border-radius:var(--iio-r-md); display:flex; align-items:center; justify-content:center; background:var(--iio-bg-3); color:var(--iio-text-3); flex-shrink:0; transition:background var(--iio-tx-med), color var(--iio-tx-med); }
.cp-hcard .ic svg{ width:17px; height:17px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.cp-hcard.ok .ic{ background:color-mix(in srgb, var(--iio-success) 14%, transparent); color:var(--iio-success); }
.cp-hcard.bad .ic{ background:color-mix(in srgb, var(--iio-critical) 12%, transparent); color:var(--iio-critical); }
.cp-hcard .tx .k{ font-size:11px; letter-spacing:.06em; text-transform:uppercase; color:var(--iio-text-3); font-weight:600; }
.cp-hcard .tx .v{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); margin-top:2px; }
/* Tile values stay in the regular text color - the icon carries the state, otherwise
   the bar turns into a rainbow. */
.cp-hcard.bad .tx .v{ color:var(--iio-critical); }

.cp-sect{ background:var(--iio-bg-2); border:1px solid var(--iio-border); border-radius:var(--iio-r-xl); padding:var(--iio-s-5) var(--iio-s-6); margin-bottom:var(--iio-s-4); box-shadow:var(--iio-shadow-sm); opacity:0; transform:translateY(6px); animation:cpRise 260ms ease-out forwards; }
.cp-sect.d1{ animation-delay:40ms; } .cp-sect.d2{ animation-delay:80ms; } .cp-sect.d3{ animation-delay:120ms; }
.cp-sect.d4{ animation-delay:160ms; } .cp-sect.d5{ animation-delay:200ms; }
.cp-sh{ display:flex; align-items:center; gap:10px; margin-bottom:var(--iio-s-5); flex-wrap:wrap; }
.cp-sh h2{ margin:0; font-size:15.5px; font-weight:700; }
.cp-sh .cnt{ font-size:12px; color:var(--iio-text-3); font-weight:600; }
.cp-sh .repo{ font-size:12px; color:var(--iio-text-3); font-weight:500; }
.cp-sh .repo b{ color:var(--iio-text-2); font-weight:600; }
.cp-sh-spacer{ flex:1; }

/* Action button in the section header: fluid but unobtrusive. It carries motion
   only on touch, not as a permanent state. */
.cp-linkbtn{ display:inline-flex; align-items:center; gap:6px; height:30px; padding:0 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-md); background:transparent; color:var(--iio-text-2); font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:color var(--iio-tx-fast), background var(--iio-tx-fast), border-color var(--iio-tx-fast), transform var(--iio-tx-fast); }
.cp-linkbtn:hover{ background:color-mix(in srgb, var(--iio-brand-ink) 6%, transparent); color:var(--iio-text-1); border-color:color-mix(in srgb, var(--iio-brand-ink) 45%, var(--iio-border)); }
.cp-linkbtn:active{ transform:translateY(1px); }
.cp-linkbtn svg{ width:13px; height:13px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
/* Label darkened toward --iio-text-1 like .iio-btn--warning: 12.5px/600 is normal
   text, and the bare token gives only 3.38:1 in light mode. The mix is 65% because
   this button can also sit on warning-tinted surfaces (a 75% mix drops to 4.35:1
   there); at 65% it is 5.08:1 (5.34:1 untinted). The border keeps the plain token. */
.cp-linkbtn.warn{ color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); border-color:color-mix(in srgb, var(--iio-warning) 40%, var(--iio-border)); }
.cp-linkbtn.warn:hover{ background:color-mix(in srgb, var(--iio-warning) 8%, transparent); color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); border-color:var(--iio-warning); }

/* Stage flow diagram */
.cp-flow{ display:flex; align-items:stretch; gap:0; overflow-x:auto; padding:4px 0 8px; }
.cp-node{ flex:1; min-width:190px; border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); padding:34px 16px 16px; position:relative; transition:border-color var(--iio-tx-med), transform var(--iio-tx-med), box-shadow var(--iio-tx-med); }
.cp-node:hover{ transform:translateY(-1px); box-shadow:var(--iio-shadow-sm); }
.cp-node.active{ border-color:var(--iio-info); box-shadow:0 0 0 3px color-mix(in srgb, var(--iio-info) 14%, transparent); }
.cp-node.missing{ opacity:.6; border-style:dashed; }
.cp-node.pending{ border-style:dashed; border-color:var(--iio-warning); }
/* Stage tab: sits in the top left corner ON the card border and belongs to the card -
   top left follows the card radius, only the bottom right corner is half rounded
   (merged into the box instead of a free-floating pill). One neutral color for all
   stages, text in the regular text color - the stage is carried by the label. */
.cp-node .stg .badge{ position:absolute; top:-1px; left:-1px; font-size:12px; font-weight:800; letter-spacing:.08em;
  padding:4px 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-lg) 0 var(--iio-r-sm) 0;
  color:var(--iio-text-1); background:var(--iio-bg-2); }
/* The tab shares its edge with the card - when the card border changes color per
   state, the tab follows, otherwise the merged corner visually tears open. */
.cp-node.pending .stg .badge{ border-color:var(--iio-warning); }
.cp-node.active .stg .badge{ border-color:var(--iio-info); }
.cp-node .inst{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); margin-top:0; }
.cp-node .env{ font-size:11.5px; color:var(--iio-text-3); margin-top:3px; }
.cp-node .runbadge{ margin-top:12px; display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:600; color:var(--iio-info); }
.cp-node .runbadge .d{ width:8px; height:8px; border-radius:50%; background:var(--iio-info); animation:cpPulse 1.2s ease-in-out infinite; }
.cp-conn{ flex:0 0 46px; display:flex; align-items:center; justify-content:center; position:relative; }
.cp-conn span{ font-size:20px; font-weight:800; color:var(--iio-border); letter-spacing:-2px; }
.cp-conn.flow span{ color:var(--iio-info); animation:cpFlow 1.1s ease-in-out infinite; }

/* Automations section */
.cp-auto-intro{ font-size:12.5px; color:var(--iio-text-2); line-height:1.5; margin:-8px 0 var(--iio-s-4); max-width:760px; }
.cp-auto-active{ border:1px solid color-mix(in srgb, var(--iio-info) 40%, var(--iio-border)); border-radius:var(--iio-r-lg); background:color-mix(in srgb, var(--iio-info) 5%, transparent); padding:14px 16px; display:flex; align-items:flex-start; gap:14px; margin-bottom:14px; transition:border-color var(--iio-tx-med), transform var(--iio-tx-med); }
.cp-auto-active:hover{ border-color:var(--iio-info); transform:translateY(-1px); }
.cp-auto-active .aic{ width:36px; height:36px; flex-shrink:0; border-radius:var(--iio-r-md); background:color-mix(in srgb, var(--iio-info) 14%, transparent); color:var(--iio-info); display:flex; align-items:center; justify-content:center; }
.cp-auto-active .aic svg{ width:18px; height:18px; stroke:currentColor; fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.cp-auto-active .abody{ flex:1; min-width:0; }
.cp-auto-active .arole{ font-size:10px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; color:var(--iio-info); }
.cp-auto-active .aname{ font-size:14.5px; font-weight:700; color:var(--iio-text-1); margin-top:3px; }
.cp-auto-active .apath{ font-size:11.5px; color:var(--iio-text-3); margin-top:3px; }
.cp-auto-active .aaction{ margin-top:8px; }
.cp-auto-list{ display:flex; flex-direction:column; }
.cp-auto-row{ display:flex; align-items:flex-start; gap:14px; padding:12px 4px; border-bottom:1px solid var(--iio-border-subtle); }
.cp-auto-row:last-child{ border-bottom:0; }
.cp-auto-row .aic{ width:28px; height:28px; flex-shrink:0; border-radius:var(--iio-r-sm); background:var(--iio-bg-3); color:var(--iio-text-3); display:flex; align-items:center; justify-content:center; margin-top:2px; }
.cp-auto-row .aic svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.cp-auto-row .ab{ flex:1; min-width:0; }
.cp-auto-row .an{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); }
.cp-auto-row .az{ font-size:12px; color:var(--iio-text-2); margin-top:3px; line-height:1.5; }
.cp-auto-row .as{ font-size:11.5px; color:var(--iio-text-3); margin-top:3px; }
.cp-auto-open{ font-size:12px; color:var(--iio-info); text-decoration:none; font-weight:600; display:inline-flex; align-items:center; gap:5px; transition:opacity var(--iio-tx-fast); }
.cp-auto-open:hover{ opacity:.75; }
.cp-auto-open svg{ width:12px; height:12px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.cp-auto-sub{ font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-text-3); margin:var(--iio-s-4) 0 var(--iio-s-2); }
.cp-auto-err{ padding:14px 16px; border:1px solid color-mix(in srgb, var(--iio-critical) 40%, var(--iio-border)); border-radius:var(--iio-r-md); background:color-mix(in srgb, var(--iio-critical) 6%, transparent); color:var(--iio-text-2); font-size:12.5px; line-height:1.5; }
.cp-auto-err b{ color:var(--iio-critical); font-weight:700; }
.cp-auto-load{ font-size:12px; color:var(--iio-text-3); display:flex; align-items:center; gap:8px; padding:6px 0; }

/* Version row: SAP base / extension */
/* Gap ONLY between rows, never after the last one. The gap is therefore
   attached to a row that follows another row.
   Do NOT use :last-of-type: it counts by element type and would only match
   the last row if no further div followed it, but the message and the hint
   come right after. The rule would never fire, and the last row would keep
   its gap below. */
.cp-upd-row{ display:flex; align-items:center; gap:12px; padding:10px 14px; border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md); background:var(--iio-bg-1); }
.cp-upd-row + .cp-upd-row{ margin-top:8px; }
.cp-upd-row .lbl{ font-size:12.5px; font-weight:600; color:var(--iio-text-1); min-width:120px; flex-shrink:0; }
.cp-upd-row .st{ flex:1; min-width:0; font-size:12px; color:var(--iio-text-2); }
.cp-upd-row .st.ok{ color:var(--iio-success); font-weight:600; }
.cp-upd-row .st.warn{ color:color-mix(in srgb, var(--iio-warning) 65%, var(--iio-text-1)); font-weight:600; }
.cp-upd-row .sha{ font-family:var(--iio-font-mono); font-size:11px; color:var(--iio-text-3); }
.cp-upd-note{ margin-top:10px; font-size:11.5px; color:var(--iio-text-3); line-height:1.5; }
.cp-upd-msg{ margin-top:8px; font-size:12px; padding:8px 12px; border-radius:var(--iio-r-sm); }
.cp-upd-msg.ok{ color:var(--iio-success); background:color-mix(in srgb, var(--iio-success) 6%, var(--iio-bg-1)); border:1px solid color-mix(in srgb, var(--iio-success) 20%, transparent); }
.cp-upd-msg.bad{ color:var(--iio-critical); background:color-mix(in srgb, var(--iio-critical) 6%, var(--iio-bg-1)); border:1px solid color-mix(in srgb, var(--iio-critical) 20%, transparent); }

/* Active runs */
.cp-active{ display:flex; flex-direction:column; gap:12px; }
/* Base of var(--iio-bg-1) instead of transparent: otherwise the card sits visually
   lower than the section and barely stands out in dark mode (a 5% tint over a
   see-through background). */
.cp-arun{ border:1px solid var(--iio-info); border-radius:var(--iio-r-lg); background:color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1)); padding:14px 16px; }
.cp-arun .top{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.cp-arun .spin{ width:16px; height:16px; border:2px solid color-mix(in srgb, var(--iio-info) 30%, transparent); border-top-color:var(--iio-info); border-radius:50%; animation:cpSpin .8s linear infinite; flex-shrink:0; }
.cp-arun .to{ font-size:13.5px; font-weight:700; color:var(--iio-text-1); }
.cp-arun .to .env{ color:var(--iio-info); }
.cp-arun .pkg{ font-size:12px; color:var(--iio-text-2); }
.cp-arun .now{ font-size:12.5px; color:var(--iio-text-2); margin-top:8px; }
.cp-arun .now b{ color:var(--iio-text-1); font-weight:600; }
.cp-idle{ text-align:center; padding:26px 16px; color:var(--iio-text-3); font-size:13px; }
.cp-idle .big{ color:var(--iio-text-2); font-size:14px; font-weight:600; margin-bottom:4px; }
.cp-rc-since{ font-size:12px; color:var(--iio-text-3); margin-bottom:12px; }
.cp-rc-list{ display:flex; flex-direction:column; }
.cp-rc-item{ display:flex; align-items:center; gap:16px; padding:11px 4px; border-bottom:1px solid var(--iio-border-subtle); }
/* Package heading between the groups. Only rendered with more than one package, so with a
   single one the list looks exactly as it did before. */
.cp-rc-pkg{ font-size:11px; font-weight:600; color:var(--iio-text-2); letter-spacing:.04em; padding:10px 4px 4px; border-top:1px solid var(--iio-border-subtle); margin-top:2px; }
.cp-rc-pkg:first-child{ border-top:0; margin-top:0; padding-top:0; }
.cp-rc-item:last-child{ border-bottom:0; }
.cp-rc-main{ flex:1; min-width:0; }
.cp-rc-main .nm{ font-size:13.5px; font-weight:600; color:var(--iio-text-1); }
.cp-rc-main .mt{ font-size:11.5px; color:var(--iio-text-3); margin-top:2px; }
.cp-rc-pushes{ font-size:12.5px; color:var(--iio-text-2); white-space:nowrap; }
.cp-rc-pushes b{ color:var(--iio-info); font-size:15px; }
.cp-rc-when{ font-size:12px; color:var(--iio-text-3); white-space:nowrap; min-width:70px; text-align:right; }
.cp-rc-foot{ display:flex; gap:12px; align-items:center; margin-top:16px; padding-top:14px; border-top:1px solid var(--iio-border-subtle); }
.cp-rc-hint{ flex:1; font-size:12.5px; color:var(--iio-text-2); }
.cp-rc-btn{ height:38px; padding:0 18px; background:var(--iio-signal); color:var(--iio-hero-text); border:1px solid var(--iio-signal); border-radius:var(--iio-r-md); font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; display:inline-flex; align-items:center; gap:8px; font-family:inherit; text-decoration:none; transition:background var(--iio-tx-fast), transform var(--iio-tx-fast), box-shadow var(--iio-tx-fast); }
/* No shadow on the button: the elevation grammar keeps buttons flat and forbids
   glows, and a colored 12px halo under the signal orange would be exactly that.
   The lift and the traveling arrow icon carry the motion without opening a
   second layer. */
.cp-rc-btn:hover{ background:var(--iio-signal-2); transform:translateY(-1px); }
.cp-rc-btn:active{ transform:translateY(0); }
.cp-rc-btn svg{ stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; transition:transform var(--iio-tx-fast); }
.cp-rc-btn:hover svg{ transform:translateX(2px); }
/* Block row instead of the approve button: in the same spot, so the two never
   show at once and the reason sits right next to the missing action.
   No fill: the border carries the meaning. A 7 percent tint would be invisible
   in dark mode anyway and would need a fixed rgba value running alongside the
   token. */
.cp-rc-block{ flex:1; min-width:0; display:flex; align-items:flex-start; gap:12px; padding:13px 15px; border:1px solid var(--iio-warning); border-radius:var(--iio-r-md); background:transparent; font-size:12.5px; line-height:1.5; color:var(--iio-text-2); }
.cp-rc-block svg{ width:16px; height:16px; stroke:var(--iio-warning); fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; margin-top:1px; }
.cp-rc-block .bt{ flex:1; min-width:0; }
.cp-rc-block .bt b{ color:var(--iio-text-1); font-weight:600; }
.cp-rc-block a{ color:var(--iio-info); font-weight:600; text-decoration:none; white-space:nowrap; }
.cp-rc-note{ margin-top:12px; font-size:12px; color:var(--iio-text-3); line-height:1.5; }

/* History */
.cp-runs{ display:flex; flex-direction:column; }
.cp-run{ border-bottom:1px solid var(--iio-border-subtle); }
.cp-run:last-child{ border-bottom:0; }
.cp-run-row{ display:flex; align-items:center; gap:14px; padding:12px 4px; cursor:pointer; transition:background var(--iio-tx-fast); }
.cp-run-row:hover{ background:var(--iio-bg-1); }
.cp-run-st{ display:inline-flex; align-items:center; gap:8px; min-width:118px; font-size:12.5px; font-weight:600; }
.cp-run-st .d{ width:9px; height:9px; border-radius:50%; background:currentColor; flex-shrink:0; }
.cp-run-st.ok{ color:var(--iio-success); } .cp-run-st.bad{ color:var(--iio-critical); }
.cp-run-st.run{ color:var(--iio-info); } .cp-run-st.run .d{ animation:cpPulse 1.2s ease-in-out infinite; }
.cp-run-st.wait{ color:var(--iio-warning); } .cp-run-st.wait .d{ animation:cpPulse 1.6s ease-in-out infinite; }
.cp-run-st.grey{ color:var(--iio-text-3); }
.cp-run-main{ flex:1; min-width:0; }
.cp-run-main .t{ font-size:13.5px; color:var(--iio-text-1); font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cp-run-main .m{ font-size:11.5px; color:var(--iio-text-3); margin-top:2px; }
.cp-run-flow{ display:flex; align-items:center; gap:8px; margin-top:5px; font-size:11.5px; flex-wrap:wrap; }
.cp-run-flow .fenv{ font-weight:700; color:var(--iio-info); font-size:10.5px; padding:1px 7px; border:1px solid var(--iio-info); border-radius:var(--iio-r-sm); background:color-mix(in srgb, var(--iio-info) 8%, transparent); }
.cp-run-flow .fenv.rel{ color:var(--iio-signal); border-color:var(--iio-signal); background:color-mix(in srgb, var(--iio-signal) 8%, transparent); }
.cp-run-flow .farr{ color:var(--iio-text-3); font-weight:800; }
.cp-run-flow .fpkg{ color:var(--iio-text-2); font-size:11px; }
.cp-run-flow .fcnt{ color:var(--iio-text-3); }
.cp-run-flow .frel{ color:var(--iio-signal); font-weight:700; letter-spacing:.06em; text-transform:uppercase; font-size:10px; }
.cp-run-metabar{ display:flex; gap:16px; flex-wrap:wrap; font-size:12px; color:var(--iio-text-3); margin-bottom:10px; padding-bottom:8px; border-bottom:1px solid var(--iio-border-subtle); }
.cp-run-metabar b{ color:var(--iio-text-2); font-weight:600; }
.cp-run-when{ font-size:12px; color:var(--iio-text-3); text-align:right; white-space:nowrap; }
.cp-run-caret{ width:16px; height:16px; color:var(--iio-text-3); transition:transform var(--iio-tx-med); flex-shrink:0; }
.cp-run-caret svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.cp-run.open .cp-run-caret{ transform:rotate(90deg); }
.cp-run-detail{ padding:4px 4px 16px 132px; opacity:0; transform:translateY(-4px); animation:cpRise 220ms ease-out forwards; }
.cp-job{ margin-bottom:12px; }
.cp-job:last-child{ margin-bottom:0; }
.cp-job .jn{ font-size:12px; font-weight:700; color:var(--iio-text-2); margin-bottom:7px; display:flex; align-items:center; gap:8px; }
.cp-job .jn .jd{ width:8px; height:8px; border-radius:50%; }
.cp-step{ display:flex; align-items:center; gap:10px; padding:4px 0 4px 16px; font-size:12.5px; border-left:1px solid var(--iio-border-subtle); margin-left:3px; }
.cp-step .sd{ width:14px; height:14px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.cp-step .sd svg{ width:13px; height:13px; stroke:currentColor; fill:none; stroke-width:2.4; stroke-linecap:round; stroke-linejoin:round; }
.cp-step.ok .sd{ color:var(--iio-success); } .cp-step.bad .sd{ color:var(--iio-critical); }
.cp-step.run .sd{ color:var(--iio-info); } .cp-step.grey .sd{ color:var(--iio-text-4); }
.cp-step .sn{ color:var(--iio-text-1); }
.cp-step.grey .sn{ color:var(--iio-text-3); }
.cp-detail-link{ display:inline-flex; align-items:center; gap:6px; margin-top:8px; font-size:12px; color:var(--iio-info); text-decoration:none; font-weight:600; transition:opacity var(--iio-tx-fast); }
.cp-detail-link:hover{ opacity:.75; }
.cp-detail-link svg{ width:13px; height:13px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.cp-detail-load{ font-size:12px; color:var(--iio-text-3); display:flex; align-items:center; gap:8px; }

.cp-empty{ text-align:center; padding:30px 16px; color:var(--iio-text-3); font-size:13px; border:1px dashed var(--iio-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-1); }
.cp-empty b{ display:block; color:var(--iio-text-2); font-size:14px; margin-bottom:4px; }
.cp-spin{ display:inline-block; width:14px; height:14px; border:2px solid var(--iio-border); border-top-color:var(--iio-info); border-radius:50%; animation:cpSpin .8s linear infinite; }
.cp-denied{ max-width:440px; margin:80px auto; text-align:center; color:var(--iio-text-2); }
.cp-denied h2{ font-size:20px; color:var(--iio-text-1); margin:0 0 8px; }

@keyframes cpSpin{ to{ transform:rotate(360deg); } }
@keyframes cpPulse{ 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
@keyframes cpFlow{ 0%{ opacity:.3; transform:translateX(-3px); } 50%{ opacity:1; } 100%{ opacity:.3; transform:translateX(3px); } }
@keyframes cpRise{ from{ opacity:0; transform:translateY(6px); } to{ opacity:1; transform:translateY(0); } }
@keyframes cpFadeIn{ from{ opacity:0; } to{ opacity:1; } }

/* Disable motion: this also covers the section reveal, or cards would stay
   stuck at opacity:0 forever. Hence forcing 'opacity:1' and 'transform:none'. */
@media(prefers-reduced-motion:reduce){
  .cp-root *{ animation:none!important; transition:none!important; }
  .cp-sect, .cp-run-detail{ opacity:1!important; transform:none!important; }
}
`;

// The stage row comes from utils/stages (DEV, QAS, PRE-PROD, PROD). Only what
// the space actually has gets rendered: an empty QAS tile in an otherwise
// deliberately short row would claim a gap that does not exist.
const CANON = STAGES.map(s => ({ stage: s.key, env: s.env }));

// The three helpers are stateless and therefore live outside the component. Since
// they produce text, they take `t` as an argument: a private copy of the catalog
// would be a second source of language strings next to the one from useTranslation.
function timeAgo(iso, t) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t("gemeinsam:zeit.geradeEben");
  const m = Math.floor(s / 60); if (m < 60) return t("zeit.vorMin", { n: m });
  const h = Math.floor(m / 60); if (h < 24) return t("zeit.vorStd", { n: h });
  const d = Math.floor(h / 24); return t("zeit.vorTagen", { n: d });
}
function duration(a, b, t) {
  if (!a) return "";
  const end = b ? new Date(b).getTime() : Date.now();
  const s = Math.max(0, Math.floor((end - new Date(a).getTime()) / 1000));
  if (s < 60) return t("dauer.sekunden", { sek: s });
  const m = Math.floor(s / 60), rest = s % 60;
  return t("dauer.minuten", { min: m, sek: rest });
}
function runStatus(r, t) {
  // Flagged by the backend as stuck (queued for over 120 minutes): treat it as
  // cancelled, or at least stop showing it as actively waiting, for an orphaned
  // run GitHub itself will not let us cancel.
  // r.status and r.conclusion are values from the GitHub response, not labels:
  // the comparison stays literal against the value, the translated line is what
  // gets displayed. In the last two branches the display falls back to the raw
  // value for lack of a mapping, so English GitHub text can show up there.
  if (r.timedOut) return { cls: "grey", label: t("zustand.zeitueberschreitung") };
  if (r.status === "queued") return { cls: "wait", label: t("zustand.warteschlange") };
  if (r.status === "in_progress") return { cls: "run", label: t("zustand.laeuft") };
  if (r.status !== "completed") return { cls: "grey", label: r.status || t("zustand.unbekannt") };
  if (r.conclusion === "success") return { cls: "ok", label: t("zustand.erfolgreich") };
  if (r.conclusion === "failure") return { cls: "bad", label: t("zustand.fehlgeschlagen") };
  if (r.conclusion === "cancelled") return { cls: "grey", label: t("zustand.abgebrochen") };
  return { cls: "grey", label: r.conclusion || t("zustand.beendet") };
}
function stepCls(s) {
  if (s.status === "in_progress") return "run";
  if (s.status !== "completed") return "grey";
  if (s.conclusion === "success") return "ok";
  if (s.conclusion === "failure") return "bad";
  return "grey";
}

export default function CicdPipeline({ user, spaces = [], onCicdRefresh }) {
  const { t, i18n } = useTranslation(["cicdmonitor", "gemeinsam"]);
  const { toasts, addToast } = useToast();
  const isAdmin = user && user.role === "admin";
  // Timestamps follow the selected language. For run times and start times this
  // is not cosmetic: "12.08.2026, 14:30" reads in English as December 8th.
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";

  const [spaceId, setSpaceId] = useState(() => (spaces[0]?.id || ""));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState({}); // runId -> {loading, run}
  // Automations are deliberately kept out of the 5s poll: loaded once per space
  // switch. The PAT budget is 5000 calls per hour, and the monitor already polls.
  const [autos, setAutos] = useState(null); // { loading, data, error }
  const [confirmUnbind, setConfirmUnbind] = useState(false); // Unbind confirmation dialog
  const [unbinding, setUnbinding] = useState(false);
  const [rerunId, setRerunId] = useState(null); // Run that is currently being restarted
  const timerRef = useRef(null);

  const load = useCallback(async (sid, silent) => {
    if (!sid) { setData(null); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const r = await api(`/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(sid)}`);
      setData(r);
    } catch (e) {
      if (!silent) addToast(e.message || t("meldung.statusNichtAbrufbar"), "error");
    }
    if (!silent) setLoading(false);
  }, [addToast, t]);

  // Restart a failed/cancelled run from the webapp (GitHub reruns the same run
  // with the same inputs). Refetch status afterwards, GitHub needs a moment.
  const rerunRun = useCallback(async (r) => {
    setRerunId(r.id);
    try {
      await api("/api/cicd/transport/rerun", { method: "POST", body: JSON.stringify({ spaceId, runId: r.id }) });
      addToast(t("meldung.neustart"), "success");
      setTimeout(() => load(spaceId, true), 2500);
    } catch (e) {
      addToast(e.message || t("meldung.neustartFehler"), "error");
    }
    setRerunId(null);
  }, [spaceId, addToast, load, t]);

  // On a direct page load the spaces are not loaded yet, so spaces[0] in useState
  // was empty and the page stayed on "no space selected" until picked manually.
  useEffect(() => {
    if (!spaceId && spaces.length > 0) setSpaceId(spaces[0].id);
  }, [spaces, spaceId]);

  useEffect(() => { if (isAdmin) load(spaceId); }, [isAdmin, spaceId, load]);

  // Automations only once per space switch. Keep out of the poll (PAT budget!).
  useEffect(() => {
    if (!isAdmin || !spaceId) { setAutos(null); return; }
    let abort = false;
    setAutos({ loading: true });
    api(`/api/cicd/repo-actions?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (!abort) setAutos({ loading: false, data: r }); })
      .catch(e => { if (!abort) setAutos({ loading: false, error: e.message || t("meldung.nichtAbrufbar") }); });
    return () => { abort = true; };
    // `t` deliberately NOT in the dependency list: this call runs exactly once
    // per space switch, a language switch must not trigger it again (PAT budget).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, spaceId]);

  // Auto refresh: 5s while a run is active, otherwise 25s. A run flagged as
  // timed out does NOT count as active, or the monitor would keep polling an
  // uncancellable orphan every 5s forever.
  const hasActive = !!data?.runs?.some(r => (r.status === "in_progress" || r.status === "queued") && !r.timedOut);
  useEffect(() => {
    if (!isAdmin || !spaceId) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load(spaceId, true), hasActive ? 5000 : 25000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isAdmin, spaceId, hasActive, load]);

  const toggleRun = async (r) => {
    if (expandedId === r.id) { setExpandedId(null); return; }
    setExpandedId(r.id);
    if (!detail[r.id]?.run) {
      setDetail(d => ({ ...d, [r.id]: { loading: true } }));
      try {
        const run = await api(`/api/cicd/transport/run/${r.id}?spaceId=${encodeURIComponent(spaceId)}`);
        setDetail(d => ({ ...d, [r.id]: { loading: false, run } }));
      } catch (e) {
        setDetail(d => ({ ...d, [r.id]: { loading: false, error: e.message } }));
      }
    }
  };

  // Keep an active run's detail open by refetching its step data live
  useEffect(() => {
    if (!expandedId || !hasActive) return;
    const r = data?.runs?.find(x => x.id === expandedId);
    if (r && (r.status === "in_progress" || r.status === "queued") && !r.timedOut) {
      api(`/api/cicd/transport/run/${expandedId}?spaceId=${encodeURIComponent(spaceId)}`)
        .then(run => setDetail(d => ({ ...d, [expandedId]: { loading: false, run } })))
        .catch(() => {});
    }
  }, [data, expandedId, hasActive, spaceId]);

  // The Escape key closes the unbind dialog. Only register the listener while it is open.
  useEffect(() => {
    if (!confirmUnbind) return;
    const h = (e) => { if (e.key === "Escape" && !unbinding) setConfirmUnbind(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [confirmUnbind, unbinding]);

  // Version upkeep: SAP base and the abat extension in the repository. Loaded
  // once per space switch (like repo-actions), NOT in the 5s poll (PAT budget).
  const [updData, setUpdData] = useState(null);
  const [updLoading, setUpdLoading] = useState(false);
  const [sapUpdating, setSapUpdating] = useState(false);
  const [extUpdating, setExtUpdating] = useState(false);
  const [updMsg, setUpdMsg] = useState(null); // { text, type:"ok"|"bad" }
  // Mode switch: rollback to SAP only
  const [confirmRueckbau, setConfirmRueckbau] = useState(false);
  const [rueckbauBusy, setRueckbauBusy] = useState(false);
  // Runs blocking the rollback. The dialog stays open and lists them - otherwise the user faces a
  // closed dialog with no idea what to wait for.
  const [rueckbauBlocker, setRueckbauBlocker] = useState([]);

  useEffect(() => {
    if (!isAdmin || !spaceId) { setUpdData(null); setUpdMsg(null); return; }
    let abort = false;
    setUpdLoading(true); setUpdMsg(null);
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => { if (!abort) setUpdData(r); })
      .catch(() => { if (!abort) setUpdData(null); })
      .finally(() => { if (!abort) setUpdLoading(false); });
    return () => { abort = true; };
  }, [isAdmin, spaceId]);

  const reloadAfterUpdate = () => {
    // Reload both sections: version status plus automations
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => setUpdData(r)).catch(() => {});
    setAutos({ loading: true });
    api(`/api/cicd/repo-actions?spaceId=${encodeURIComponent(spaceId)}`)
      .then(x => setAutos({ loading: false, data: x }))
      .catch(e => setAutos({ loading: false, error: e.message || t("meldung.nichtAbrufbar") }));
    // Refresh the health cards too
    load(spaceId, true);
  };

  // Liveness check for the GitHub key: an expired PAT used to show up only as scattered
  // 401 errors. The banner at the top names the cause and offers both ways out: store a
  // new key, or confirm the extension of the existing one.
  const [patCheck, setPatCheck] = useState(null);
  const [patNeu, setPatNeu] = useState("");
  const [patBusy, setPatBusy] = useState(false);
  const [patMsg, setPatMsg] = useState(null);
  useEffect(() => {
    // The page redirects non-admins before the banner - the check would be a wasted
    // GitHub round trip.
    if (!isAdmin) return;
    let abort = false;
    api("/api/cicd/github-token-check")
      .then(r => { if (!abort) setPatCheck(r); })
      .catch(() => { if (!abort) setPatCheck(null); });
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  const patProblem = !!(patCheck?.tokenSet && patCheck.ok === false);
  const savePat = async () => {
    setPatBusy(true); setPatMsg(null);
    try {
      await api("/api/cicd/github-token", { method: "POST", body: JSON.stringify({ token: patNeu.trim() }) });
      setPatNeu("");
      setPatCheck({ tokenSet: true, ok: true });
      setPatMsg({ type: "ok", text: t("pat.gespeichert") });
      reloadAfterUpdate();
    } catch (e) {
      setPatMsg({ type: "bad", text: e.message || t("pat.fehlgeschlagen") });
    }
    setPatBusy(false);
  };
  const recheckPat = async () => {
    setPatBusy(true); setPatMsg(null);
    try {
      const r = await api("/api/cicd/github-token-check");
      setPatCheck(r);
      if (r?.ok) { setPatMsg(null); reloadAfterUpdate(); }
      else setPatMsg({ type: "bad", text: t("pat.weiterAbgelehnt", { status: r?.status ?? "?" }) });
    } catch (e) {
      setPatMsg({ type: "bad", text: e.message || t("pat.fehlgeschlagen") });
    }
    setPatBusy(false);
  };

  // "Check for updates": asks the server again - it compares the customer state with the
  // state we approved and reports a newer SAP HEAD transparently as not yet approved.
  const checkUpdates = () => {
    setUpdLoading(true); setUpdMsg(null);
    api(`/api/cicd/repo-update-status?spaceId=${encodeURIComponent(spaceId)}`)
      .then(r => setUpdData(r))
      .catch(() => setUpdData(null))
      .finally(() => setUpdLoading(false));
  };

  const updateSapBase = async () => {
    setSapUpdating(true); setUpdMsg(null);
    try {
      const r = await api("/api/cicd/update-sap-base", { method: "POST", body: JSON.stringify({ spaceId }) });
      setUpdMsg({ text: r.geaendert ? t("version.sapAktualisiert", { sha: r.nachSha }) : t("version.warAktuell"), type: "ok" });
      reloadAfterUpdate();
    } catch (e) {
      setUpdMsg({ text: e.message || t("version.fehlgeschlagen"), type: "bad" });
    }
    setSapUpdating(false);
  };

  const updateExtension = async () => {
    setExtUpdating(true); setUpdMsg(null);
    try {
      const r = await api("/api/cicd/update-extension", { method: "POST", body: JSON.stringify({ spaceId }) });
      setUpdMsg({ text: r.geaendert ? t("version.erwGehoben", { version: r.nachVersion }) : t("version.warAktuell"), type: "ok" });
      reloadAfterUpdate();
    } catch (e) {
      setUpdMsg({ text: e.message || t("version.fehlgeschlagen"), type: "bad" });
    }
    setExtUpdating(false);
  };

  // Rollback: remove the extension files, switch to SAP-only mode.
  const doRueckbau = async () => {
    setRueckbauBusy(true);
    try {
      const r = await api("/api/cicd/remove-extension", { method: "POST", body: JSON.stringify({ spaceId }) });
      if (r.ok) {
        addToast(t("modus.rueckbauErfolg"), "success");
        setConfirmRueckbau(false);
        reloadAfterUpdate();
      } else {
        addToast(r.error || t("modus.rueckbauFehler"), "error");
      }
    } catch (e) {
      // 409 with this code: something is still running. Keep the dialog open and name the runs.
      if (e.code === "TRANSPORT_RUNNING") {
        setRueckbauBlocker(Array.isArray(e.body?.laufend) ? e.body.laufend : []);
        addToast(t("modus.rueckbauGesperrt"), "error");
      } else {
        addToast(e.message || t("modus.rueckbauFehler"), "error");
        setConfirmRueckbau(false);
      }
    }
    setRueckbauBusy(false);
  };

  // Escape closes the rollback dialog
  useEffect(() => {
    if (!confirmRueckbau) return;
    const h = (e) => { if (e.key === "Escape" && !rueckbauBusy) setConfirmRueckbau(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [confirmRueckbau, rueckbauBusy]);

  const doUnbind = async () => {
    setUnbinding(true);
    try {
      const r = await api("/api/cicd/unbind", { method: "POST", body: JSON.stringify({ spaceId }) });
      addToast(t("meldung.verbindungGeloest", { repo: r.repo }), "success");
      setConfirmUnbind(false);
      load(spaceId);
      // Loading state, NOT null: until load() answers (one round trip), the
      // section would otherwise render its empty branch and claim "no workflows
      // found, the content repository has not shipped any automations yet".
      // That would be the wrong reason: there is simply no binding anymore, the
      // repository itself is unchanged. The section disappears afterwards anyway
      // once configured turns false.
      setAutos({ loading: true });
      // The CI/CD marker on spaces (sidebar, cards, space header) depends on a
      // call that otherwise only runs at startup. Without this refetch it kept
      // showing the unbound space as still configured.
      onCicdRefresh?.();
    } catch (e) {
      addToast(e.message || t("meldung.verbindungFehler"), "error");
    } finally {
      setUnbinding(false);
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="cp-root"><style>{CSS}</style>
        <div className="cp-denied"><h2>{t("zugriff.titel")}</h2><p>{t("zugriff.text")}</p></div>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  const health = data?.health || {};
  const project = data?.project;
  const stages = data?.stages || [];
  const runs = data?.runs || [];
  const active = data?.active || [];
  const activeEnvs = new Set(active.map(a => a.targetEnv).filter(Boolean));
  const rc = data?.releaseCandidate;
  // Configured but disconnected from GitHub: the history is still there, but
  // the pipeline cannot act. The release approval page uses the same
  // distinction (tokenSet, not configured).
  const getrennt = data?.configured === true && health.tokenSet === false;
  const runsUnknown = !!data?.runsUnknown;
  const gotoSetup = (e) => { e.preventDefault(); window.history.pushState({}, "", "/cicd-setup"); window.dispatchEvent(new PopStateEvent("popstate")); };

  const configured = data?.configured === true;
  const repoLabel = project ? `${project.repoOwner}/${project.contentRepo}` : "";

  // Stage flow built from the canonical DEV/QAS/PROD row, enriched with the
  // instances that actually exist. Only stages the space really has. If none
  // are tagged, the row shows the full template, where "missing" is the
  // correct statement.
  const vorhandeneStufen = new Set(stages.map(x => x.stage));
  const reihe = vorhandeneStufen.size > 0 ? CANON.filter(c => vorhandeneStufen.has(c.stage)) : CANON;
  const flowNodes = reihe.map(c => {
    const s = stages.find(x => x.stage === c.stage);
    const present = !!s;
    const credsReady = !!s?.credsReady;
    const synced = !!s?.synced; // The GitHub environment really exists
    const state = !present ? "missing" : (synced && credsReady ? "ready" : (credsReady ? "pending" : "incomplete"));
    return { stage: c.stage, env: c.env, instanceName: s?.instanceName || null, present, synced, credsReady, state, active: activeEnvs.has(s?.targetEnv || c.env) };
  });

  const hc = (ok, bad) => ok ? "ok" : (bad ? "bad" : "");

  const autoData = autos?.data;
  const autoAktiv = autoData?.aktiv || [];
  const autoVorlagen = autoData?.vorlagen || [];
  const autoHaupt = autoAktiv.find(w => w.wirdGefahren) || null;
  const autoRest = autoAktiv.filter(w => w !== autoHaupt);

  return (
    <div className="cp-root">
      <style>{CSS}</style>

      <div className="cp-ph">
        <div className="crumb">{t("kopf.brotkrumeAdmin")} <b>&gt;&gt;</b> {t("kopf.brotkrumeCicd")} <b>&gt;&gt;</b> {t("kopf.brotkrumeAktuell")}</div>
        <h1>{t("kopf.titel")}</h1>
        <div className="sub">{t("kopf.sub")}</div>
      </div>

      <div className="cp-bar">
        <select className="cp-sel" value={spaceId} onChange={e => { setSpaceId(e.target.value); setExpandedId(null); }}>
          <option value="">{t("leiste.spaceWaehlen")}</option>
          {spaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
        </select>
        <button type="button" className="cp-refresh" onClick={() => load(spaceId)} disabled={!spaceId || loading}>
          <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          {t("gemeinsam:aktion.aktualisieren")}
        </button>
        <span className={"cp-live" + (hasActive ? " on" : "")}>
          <span className="beat" />
          {hasActive ? t("leiste.liveAktiv")
            : getrennt ? t("leiste.githubGetrennt")
            : runsUnknown && data ? t("leiste.githubUnerreichbar")
            : t("leiste.leerlauf")}
        </span>
      </div>

      {!spaceId ? (
        <div className="cp-empty"><b>{t("leer.keinSpaceTitel")}</b>{t("leer.keinSpaceText")}</div>
      ) : loading && !data ? (
        <div className="cp-empty"><span className="cp-spin" /> {t("leer.laedt")}</div>
      ) : data && data.configured === false ? (
        /* If this is reached right after unbinding, the unbind dialog just said "the
           connection can be re-established any time via CI/CD Setup", so the link here
           must be clickable too, or that promised path falls through. Same link style
           as the disconnected state of the release candidate (a onClick={gotoSetup}). */
        <div className="cp-empty">
          <b>{t("leer.nichtEingerichtetTitel")}</b>
          <Trans t={t} i18nKey="leer.nichtEingerichtetText" components={{
            setup: <a href="/cicd-setup" onClick={gotoSetup} style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>CI/CD Setup</a>,
          }} />
        </div>
      ) : (
        <>
          {/* Health bar */}
          {/* Expired/revoked PAT: name the cause at the very top before the follow-up
              401s obscure the picture. role=alert so screen readers announce it first. */}
          {patProblem && (
            <div className="cp-pat-banner" role="alert">
              <div className="ttl">{t("pat.titel")}</div>
              <div className="txt">{t("pat.text")}</div>
              <div className="row">
                <input type="password" className="iio-input" style={{ maxWidth: 420 }} value={patNeu}
                  onChange={e => setPatNeu(e.target.value)} placeholder={t("pat.platzhalter")}
                  autoComplete="off" spellCheck={false}/>
                <button type="button" className="iio-btn iio-btn--signal" disabled={!patNeu.trim() || patBusy} onClick={savePat}>
                  {t("pat.speichern")}
                </button>
                <button type="button" className="iio-btn iio-btn--secondary" disabled={patBusy} onClick={recheckPat}>
                  {t("pat.verlaengert")}
                </button>
              </div>
              {patMsg && <div className={"msg " + patMsg.type}>{patMsg.text}</div>}
            </div>
          )}
          <div className="cp-health">
            <div className={"cp-hcard " + hc(health.tokenSet, !health.tokenSet)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M9 19c-4 1.5-4-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6a4.6 4.6 0 0 0-1.3-3.2 4.3 4.3 0 0 0-.1-3.2s-1-.3-3.4 1.3a11.7 11.7 0 0 0-6.2 0C6.6 2.8 5.6 3.1 5.6 3.1a4.3 4.3 0 0 0-.1 3.2 4.6 4.6 0 0 0-1.3 3.2c0 4.6 2.7 5.7 5.5 6-.6.6-.6 1.2-.5 2V21"/></svg></div>
              <div className="tx"><div className="k">{t("health.github")}</div><div className="v">{health.login || t("health.githubNichtVerbunden")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.repoPrivate === true, health.repoPrivate === false)}>
              <div className="ic"><svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
              <div className="tx"><div className="k">{t("health.repository")}</div><div className="v">{health.repoPrivate === true ? t("health.repoPrivat") : health.repoPrivate === false ? t("health.repoOeffentlich") : t("health.unbekannt")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.workflowActive === true, health.workflowActive === false)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10.01-3-3"/></svg></div>
              <div className="tx"><div className="k">{t("health.workflow")}</div><div className="v">{health.workflowActive === true ? t("health.workflowAktiv") : health.workflowActive === false ? t("health.workflowInaktiv") : t("health.unbekannt")}</div></div>
            </div>
            <div className={"cp-hcard " + hc(health.stagesReady, !health.stagesReady)}>
              <div className="ic"><svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg></div>
              <div className="tx"><div className="k">{t("health.stages")}</div><div className="v">{health.stagesReady ? t("health.stagesBereit") : t("health.stagesUnvollstaendig")}</div></div>
            </div>
          </div>

          {/* Stage flow diagram: also carries the repo label and the unbind action.
              The button sits next to the repo so the binding can be dissolved exactly
              where it is visible, not on a separate management page. */}
          <div className="cp-sect d1">
            <div className="cp-sh">
              <h2>{t("fluss.titel")}</h2>
              {repoLabel && <span className="repo">{t("fluss.repo")} <b>{repoLabel}</b></span>}
              <div className="cp-sh-spacer" />
              {configured && (
                <button type="button" className="cp-linkbtn warn" onClick={() => setConfirmUnbind(true)} title={t("fluss.verbindungLoesenTitel")}>
                  <svg viewBox="0 0 24 24"><path d="M18.84 12.25 15 16.09a5 5 0 0 1-7.07 0"/><path d="M5.16 11.75 9 7.91a5 5 0 0 1 7.07 0"/><path d="M2 2l20 20"/></svg>
                  {t("fluss.verbindungLoesen")}
                </button>
              )}
            </div>
            <div className="cp-flow">
              {flowNodes.map((n, i) => (
                <div key={n.stage} style={{ display: "contents" }}>
                  {i > 0 && <div className={"cp-conn" + (n.active ? " flow" : "")}><span>&gt;&gt;</span></div>}
                  <div className={"cp-node " + n.stage + (n.active ? " active" : "") + (n.state === "missing" ? " missing" : (n.state === "pending" ? " pending" : ""))}>
                    <div className="stg">
                      {/* Label via stageLabel, not via n.env: n.env is the name of the
                          GitHub environment and gets passed through below as a value.
                          Today both hold the same text, but they mean two different things. */}
                      <span className="badge">{stageLabel(n.stage)}</span>
                      {/* No separate status dot next to the tab: the env line below says
                          the same thing - the dot was pure duplication. */}
                    </div>
                    <div className="inst">{n.instanceName || t("fluss.keineInstanz")}</div>
                    <div className="env">{!n.present ? t("fluss.nichtGetaggt") : n.synced ? t("fluss.environment", { env: n.env }) : t("fluss.nochNichtSynchronisiert")}</div>
                    {n.active && <div className="runbadge"><span className="d" />{t("fluss.transportLaeuft")}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Automations in the repository. Loaded once per space, hence placed here
              statically between the stage flow and the release candidate: the content
              relates to the repository (which workflows live there, which one we run),
              less so to the run history. */}
          <div className="cp-sect d2">
            <div className="cp-sh">
              <h2>{t("auto.titel")}</h2>
              {autoData?.contentBranch && <span className="cnt">{t("auto.branch", { name: autoData.contentBranch })}</span>}
            </div>
            <div className="cp-auto-intro">
              {t("auto.intro")}
            </div>

            {/* Version upkeep: SAP base and the abat extension. Loaded once per space
                switch, NOT in the poll. Covers both together instead of a single
                extension button. */}
            {updLoading ? (
              <div className="cp-auto-load"><span className="cp-spin" /> {t("version.laedt")}</div>
            ) : updData?.configured ? (
              /* zustand carries the German values "aktuell", "veraltet", "fehlt", "unbekannt":
                 these are values from the server's response, not labels. The comparison
                 stays literal against them, the translated line is what gets displayed. */
              <>
                {updData.transportModus !== "sap" && (
                <div className="cp-upd-row">
                  <button type="button" className="cp-linkbtn" onClick={checkUpdates} disabled={sapUpdating || extUpdating || updLoading}>
                    {t("version.suchen")}
                  </button>
                </div>
                )}
                {updData.transportModus !== "sap" && updData.sapBasis?.applicable && (
                  <div className="cp-upd-row">
                    <span className="lbl">{t("version.sapBasis")}</span>
                    <span className={"st" + ((updData.sapBasis.zustand === "aktuell" || updData.sapBasis.zustand === "ungeprueft") ? " ok" : updData.sapBasis.zustand === "veraltet" ? " warn" : "")}>
                      {(updData.sapBasis.zustand === "aktuell" || updData.sapBasis.zustand === "ungeprueft") && (
                        <>{t("version.aktuell")} <span className="sha">{t("version.standSha", { sha: updData.sapBasis.installedShortSha })}</span></>
                      )}
                      {updData.sapBasis.zustand === "veraltet" && (
                        <>{t("version.aktualisierungVerfuegbar")} <span className="sha">{t("version.sapStandNeu", { installiert: updData.sapBasis.installedShortSha, neu: updData.sapBasis.freigegebenShortSha || updData.sapBasis.latestShortSha })}</span></>
                      )}
                      {updData.sapBasis.zustand === "unbekannt" && (
                        <span style={{ color: "var(--iio-text-3)" }}>{t("version.standUnbekannt")}</span>
                      )}
                    </span>
                    {/* Transparency instead of silent withholding: a newer SAP state without
                        our approval is visible but deliberately not clickable - the note
                        says why and when it arrives. */}
                    {updData.sapBasis.zustand === "ungeprueft" && (
                      <>
                        <span className="sha" style={{ color: "var(--iio-text-3)" }} title={t("version.ungeprueftHinweis")}>
                          {t("version.ungeprueft", { sha: updData.sapBasis.latestShortSha })}
                        </span>
                        <button type="button" className="cp-linkbtn" disabled title={t("version.ungeprueftHinweis")}>
                          {t("gemeinsam:aktion.aktualisieren")}
                        </button>
                      </>
                    )}
                    {updData.sapBasis.zustand === "veraltet" && (
                      <button type="button" className="cp-linkbtn warn" onClick={updateSapBase} disabled={sapUpdating || extUpdating}>
                        {sapUpdating ? <span className="cp-spin" style={{ width: 12, height: 12 }} /> : null}
                        {t("gemeinsam:aktion.aktualisieren")}
                      </button>
                    )}
                  </div>
                )}

                {updData.transportModus !== "sap" && (
                <div className="cp-upd-row">
                  <span className="lbl">{t("version.erweiterung")}</span>
                  <span className={"st" + (updData.erweiterung?.zustand === "aktuell" ? " ok" : updData.erweiterung?.zustand === "veraltet" ? " warn" : "")}>
                    {updData.erweiterung?.zustand === "aktuell" && (
                      <>{t("version.erwAktuell", { version: updData.erweiterung.installedVersion })}</>
                    )}
                    {updData.erweiterung?.zustand === "veraltet" && (
                      <>{t("version.erwVerfuegbar", { version: updData.erweiterung.installedVersion, neu: updData.erweiterung.aktuelleVersion })}</>
                    )}
                    {updData.erweiterung?.zustand === "fehlt" && (
                      <>{t("version.erwFehlt")}</>
                    )}
                    {updData.erweiterung?.zustand === "unbekannt" && (
                      <span style={{ color: "var(--iio-text-3)" }}>{t("version.standUnbekannt")}</span>
                    )}
                  </span>
                  {(updData.erweiterung?.zustand === "veraltet" || updData.erweiterung?.zustand === "fehlt") && (
                    <button type="button" className="cp-linkbtn warn" onClick={updateExtension} disabled={extUpdating || sapUpdating}>
                      {extUpdating ? <span className="cp-spin" style={{ width: 12, height: 12 }} /> : null}
                      {updData.erweiterung?.zustand === "fehlt" ? t("version.installieren") : t("gemeinsam:aktion.aktualisieren")}
                    </button>
                  )}
                </div>
                )}

                {/* Visible instead of title-only: touch devices have no hover, the reason
                    for the locked button must be readable there too. */}
                {updData.transportModus !== "sap" && updData.sapBasis?.zustand === "ungeprueft" && (
                  <div className="cp-upd-note">{t("version.ungeprueftHinweis")}</div>
                )}

                {updMsg && (
                  <div className={"cp-upd-msg " + updMsg.type}>{updMsg.text}</div>
                )}

                {updData.transportModus !== "sap" && (
                  <div className="cp-upd-note">
                    {t("version.hinweis")}
                  </div>
                )}

                {/* Transport mode: visible at a glance and switchable. The value comes from the
                    same endpoint as the version data (repo-update-status). */}
                {updData.transportModus && (
                  <div className="cp-upd-row" style={{ marginTop: 14, borderTop: "1px solid var(--iio-border-subtle)", paddingTop: 14 }}>
                    <span className="lbl">{t("modus.titel")}</span>
                    <span className={"st" + (updData.transportModus === "erweitert" ? " ok" : "")}>
                      {updData.transportModus === "erweitert" ? t("modus.erweitert") : t("modus.sap")}
                    </span>
                    {updData.transportModus === "erweitert" ? (
                      active.length > 0
                        ? <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>{t("modus.gesperrt")}</span>
                        : <button type="button" className="cp-linkbtn warn" onClick={() => { setRueckbauBlocker([]); setConfirmRueckbau(true); }} disabled={rueckbauBusy || sapUpdating || extUpdating}>
                            {t("modus.aufSapWechseln")}
                          </button>
                    ) : (
                      <button type="button" className="cp-linkbtn" onClick={updateExtension} disabled={extUpdating || sapUpdating}>
                        {extUpdating ? <span className="cp-spin" style={{ width: 12, height: 12 }} /> : null}
                        {t("modus.aufErweitertWechseln")}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : null}
            {autos?.loading ? (
              <div className="cp-auto-load"><span className="cp-spin" /> {t("auto.laedt")}</div>
            ) : autos?.error ? (
              <div className="cp-auto-err"><b>{t("auto.nichtAbrufbar")}</b> {autos.error}</div>
            ) : autoData?.fehler ? (
              <div className="cp-auto-err"><b>{t("auto.workflowsNichtGeliefert")}</b> {autoData.fehler}</div>
            ) : (
              <>
                {autoHaupt ? (
                  <div className="cp-auto-active">
                    <div className="aic">
                      <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m22 4-10 10.01-3-3"/></svg>
                    </div>
                    <div className="abody">
                      <div className="arole">{t("auto.faehrt")}</div>
                      <div className="aname">{autoHaupt.name}</div>
                      {/* zustand is the GitHub value ("active", "disabled_manually", ...). Only
                          the most common case has a label, otherwise the raw value shows. */}
                      <div className="apath">{autoHaupt.pfad} · {t("auto.zustand", { wert: autoHaupt.zustand === "active" ? t("auto.zustandAktiv") : autoHaupt.zustand })}</div>
                      {autoHaupt.htmlUrl && (
                        <div className="aaction">
                          <a className="cp-auto-open" href={autoHaupt.htmlUrl} target="_blank" rel="noreferrer">
                            {t("auto.inGithubOeffnen")}
                            <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6M10 14 21 3"/></svg>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : autoAktiv.length === 0 && autoVorlagen.length === 0 ? (
                  <div className="cp-idle" style={{ padding: "18px 12px" }}>
                    <div className="big">{t("auto.keineWorkflowsTitel")}</div>
                    {t("auto.keineWorkflowsText")}
                  </div>
                ) : null}

                {autoRest.length > 0 && (
                  <>
                    <div className="cp-auto-sub">{t("auto.weitereWorkflows")}</div>
                    <div className="cp-auto-list">
                      {autoRest.map(w => (
                        <div className="cp-auto-row" key={w.pfad}>
                          <div className="aic">
                            <svg viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                          </div>
                          <div className="ab">
                            <div className="an">{w.name}</div>
                            <div className="as">
                              {w.pfad} · {t("auto.zustand", { wert: w.zustand === "active" ? t("auto.zustandAktiv") : w.zustand })} · {t("auto.laeuftNichtMit")}
                            </div>
                          </div>
                          {w.htmlUrl && (
                            <a className="cp-auto-open" href={w.htmlUrl} target="_blank" rel="noreferrer">
                              {t("gemeinsam:aktion.oeffnen")}
                              <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6M10 14 21 3"/></svg>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {autoVorlagen.length > 0 && (
                  <>
                    <div className="cp-auto-sub">{t("auto.sapVorlagen")}</div>
                    <div className="cp-auto-list">
                      {autoVorlagen.map(v => (
                        <div className="cp-auto-row" key={v.name}>
                          <div className="aic">
                            <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 10h8M8 14h5"/></svg>
                          </div>
                          <div className="ab">
                            <div className="an">{v.name}</div>
                            {v.zweck && <div className="az">{v.zweck}</div>}
                            {v.status && <div className="as">{v.status}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Release candidate: what has piled up since the last PROD release, plus approval */}
          {rc && (
            <div className="cp-sect d3">
              <div className="cp-sh">
                <h2>{t("rc.titel")}</h2>
                <span className="cnt">
                  {(rc.summary?.pakete || 0) > 1 && <>{t("rc.pakete", { count: rc.summary.pakete })}{" · "}</>}
                  {t("rc.iflows", { count: rc.summary?.iflows || 0 })}
                  {" · "}{t("rc.laeufe", { count: rc.summary?.laeufe || 0 })}
                </span>
              </div>
              {(rc.kandidaten?.length || 0) === 0 ? (
                <div className="cp-idle">
                  <div className="big">{t("rc.nichtsOffenTitel")}</div>
                  {t("rc.nichtsOffenText")}
                </div>
              ) : (
                <>
                  <div className="cp-rc-since">
                    {rc.lastRelease ? t("rc.seitRelease", { tag: rc.lastRelease.tag, zeit: timeAgo(rc.lastRelease.at, t) }) : t("rc.seitBeginn")}
                    {(rc.ohnePaket || 0) > 0 ? " · " + t("rc.ohnePaket", { count: rc.ohnePaket }) : ""}
                  </div>
                  <div className="cp-rc-list">
                    {/* One group per package. Every plant is its own package and is released on
                        its own, so the counts must not be merged across them. */}
                    {rc.kandidaten.map(k => (
                      <Fragment key={k.packageId}>
                        {rc.kandidaten.length > 1 && (
                          <div className="cp-rc-pkg">{k.packageName || k.packageId}</div>
                        )}
                        {k.items.map(it => (
                          <div className="cp-rc-item" key={k.packageId + "/" + it.iflow}>
                            <div className="cp-rc-main">
                              <div className="nm">{it.iflow}</div>
                              <div className="mt">{it.packageId}</div>
                            </div>
                            {/* DEV >> QAS are stage labels, not prose: they come from stageLabel
                                and read the same in both languages. */}
                            <div className="cp-rc-pushes"><b>{it.pushes}×</b> {stageLabel("dev")} &gt;&gt; {stageLabel("qas")}</div>
                            <div className="cp-rc-when">{timeAgo(it.lastAt, t)}</div>
                          </div>
                        ))}
                      </Fragment>
                    ))}
                  </div>
                  {/* Three variants in the same spot, each with its own key: the footer
                      changes while the page is open (auto refresh), and without a key React
                      would relabel the same node instead of swapping it. */}
                  <div className="cp-rc-foot">
                    {getrennt ? (
                      <div className="cp-rc-block" key="getrennt">
                        <svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>
                        <span className="bt">
                          <Trans t={t} i18nKey="rc.getrennt" components={{ b: <b /> }} />
                        </span>
                        <a href="/cicd-setup" onClick={gotoSetup}>{t("rc.zumSetup")}</a>
                      </div>
                    ) : (
                      <Fragment key="freigabe">
                        <span className="cp-rc-hint">{t("rc.freigabeHinweis")}</span>
                        <a className="cp-rc-btn" href="/transport-freigabe" onClick={(e) => { e.preventDefault(); window.history.pushState({}, "", "/transport-freigabe"); window.dispatchEvent(new PopStateEvent("popstate")); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                          {t("rc.freigabeKnopf")}
                        </a>
                      </Fragment>
                    )}
                  </div>
                </>
              )}
              {rc.ohneZuordnung > 0 && (
                <div className="cp-rc-note">
                  {t("rc.ohneZuordnung", { count: rc.ohneZuordnung })}
                </div>
              )}
            </div>
          )}

          <div className="cp-sect d4">
            <div className="cp-sh"><h2>{t("aktiv.titel")}</h2><span className="cnt">{active.length ? t("aktiv.anzahl", { n: active.length }) : ""}</span></div>
            {active.length === 0 ? (
              runsUnknown ? (
                <div className="cp-idle">
                  <div className="big">{t("aktiv.nichtAbrufbarTitel")}</div>
                  {t("aktiv.nichtAbrufbarText")}
                </div>
              ) : (
                <div className="cp-idle"><div className="big">{t("aktiv.keinTransportTitel")}</div>{t("aktiv.keinTransportText")}</div>
              )
            ) : (
              <div className="cp-active">
                {active.map(a => (
                  <div className="cp-arun" key={a.id}>
                    <div className="top">
                      <span className="spin" />
                      <span className="to">{t("aktiv.transportNach")} <span className="env">{a.targetEnv || "?"}</span></span>
                      {a.packageId && <span className="pkg">{a.packageId}</span>}
                    </div>
                    <div className="now">
                      {a.currentStep
                        ? <>{t("aktiv.aktuellerSchritt")} <b>{a.currentStep}</b>{a.currentJob ? ` (${a.currentJob})` : ""}</>
                        : a.currentJob ? <>{t("aktiv.aktuellerJob")} <b>{a.currentJob}</b></> : t("aktiv.wirdVorbereitet")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          <div className="cp-sect d5">
            <div className="cp-sh"><h2>{t("lauf.titel")}</h2><span className="cnt">{runs.length ? t("lauf.letzte", { n: runs.length }) : ""}</span></div>
            {runs.length === 0 ? (
              /* "No runs" is a statement about GitHub. It may only appear here when
                 GitHub actually answered, otherwise an empty list would claim something
                 about a history the webapp never read. */
              runsUnknown ? (
                <div className="cp-empty">
                  <b>{t("lauf.nichtAbrufbarTitel")}</b>
                  {getrennt
                    ? t("lauf.nichtAbrufbarGetrennt")
                    : (data?.githubError || t("lauf.nichtAbrufbarFehler"))}
                </div>
              ) : (
                <div className="cp-empty"><b>{t("lauf.keineTitel")}</b>{t("lauf.keineText")}</div>
              )
            ) : (
              <div className="cp-runs">
                {runs.map(r => {
                  const st = runStatus(r, t);
                  const open = expandedId === r.id;
                  const d = detail[r.id];
                  return (
                    <div className={"cp-run" + (open ? " open" : "")} key={r.id}>
                      <div className="cp-run-row" onClick={() => toggleRun(r)}>
                        <span className={"cp-run-st " + st.cls}><span className="d" />{st.label}</span>
                        <div className="cp-run-main">
                          <div className="t">{r.title || t("lauf.standard")}</div>
                          <div className="m">#{r.runNumber}{r.actor ? ` - ${r.actor}` : ""}{r.event ? ` - ${r.event}` : ""}</div>
                          {r.meta && (
                            <div className="cp-run-flow">
                              <span className="fenv">{r.meta.sourceEnv || "?"}</span>
                              <span className="farr">&gt;&gt;</span>
                              <span className={"fenv" + (r.meta.kind === "release" ? " rel" : "")}>{r.meta.targetEnv || "?"}</span>
                              {r.meta.packageId && <span className="fpkg">{r.meta.packageId}</span>}
                              {r.meta.artifacts?.length ? <span className="fcnt">{t("lauf.iflowAnzahl", { count: r.meta.artifacts.length })}</span> : null}
                              {r.meta.kind === "release" && <span className="frel">{t("lauf.release")}</span>}
                            </div>
                          )}
                        </div>
                        <div className="cp-run-when">
                          {timeAgo(r.runStartedAt || r.createdAt, t)}<br />
                          <span className="dur">{duration(r.runStartedAt || r.createdAt, r.status === "completed" ? r.updatedAt : null, t)}</span>
                        </div>
                        <span className="cp-run-caret"><svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg></span>
                      </div>
                      {open && (
                        <div className="cp-run-detail">
                          {r.meta && (
                            <div className="cp-run-metabar">
                              <span>{r.meta.kind === "release" ? t("lauf.releaseTag") : t("lauf.branch")}: <b>{r.meta.ref || "-"}</b></span>
                              {r.meta.artifacts?.length ? <span>{t("lauf.artefakte")}: <b>{r.meta.artifacts.join(", ")}</b></span> : null}
                              {r.meta.at ? <span>{new Date(r.meta.at).toLocaleString(gebiet)}</span> : null}
                            </div>
                          )}
                          {d?.loading ? (
                            <div className="cp-detail-load"><span className="cp-spin" /> {t("lauf.ladeSchritte")}</div>
                          ) : d?.error ? (
                            <div className="cp-detail-load" style={{ color: "var(--iio-critical)" }}>{d.error}</div>
                          ) : d?.run ? (
                            <>
                              {(d.run.jobs || []).map((j, ji) => {
                                const jc = stepCls({ status: j.status, conclusion: j.conclusion });
                                const jcol = jc === "ok" ? "var(--iio-success)" : jc === "bad" ? "var(--iio-critical)" : jc === "run" ? "var(--iio-info)" : "var(--iio-text-4)";
                                return (
                                  <div className="cp-job" key={ji}>
                                    <div className="jn"><span className="jd" style={{ background: jcol }} />{j.name}</div>
                                    {(j.steps || []).map((s, si) => {
                                      const sc = stepCls(s);
                                      return (
                                        <div className={"cp-step " + sc} key={si}>
                                          <span className="sd">
                                            {sc === "ok" ? <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
                                              : sc === "bad" ? <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>
                                              : sc === "run" ? <span className="cp-spin" style={{ width: 12, height: 12 }} />
                                              : <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/></svg>}
                                          </span>
                                          <span className="sn">{s.name}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                              <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
                                {(r.conclusion === "failure" || r.conclusion === "cancelled") && (
                                  <button type="button" className="cp-detail-link" disabled={rerunId === r.id}
                                    onClick={(e) => { e.stopPropagation(); rerunRun(r); }}
                                    style={{ background: "none", border: "none", cursor: rerunId === r.id ? "default" : "pointer", font: "inherit" }}>
                                    {rerunId === r.id
                                      ? <span className="cp-spin" style={{ width: 12, height: 12 }} />
                                      : <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>}
                                    {t("lauf.erneutVersuchen")}
                                  </button>
                                )}
                                {r.htmlUrl && <a className="cp-detail-link" href={r.htmlUrl} target="_blank" rel="noreferrer">
                                  <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6M10 14 21 3"/></svg>
                                  {t("lauf.inGithubOeffnen")}
                                </a>}
                              </div>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Unbind confirmation dialog: implemented natively as .iio-modal, NOT a MUI
          portal (that loses the .iio scope and breaks colors in dark mode). */}
      {confirmUnbind && (
        <div className="iio-modal-overlay is-open"
          onMouseDown={e => { if (e.target === e.currentTarget && !unbinding) setConfirmUnbind(false); }}>
          <div className="iio-modal" role="dialog" aria-modal="true" aria-labelledby="cp-unbind-t"
            onMouseDown={e => e.stopPropagation()}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("loesen.eyebrow", { name: spaces.find(s => s.id === spaceId)?.name || "" })}</span>
                <h2 id="cp-unbind-t">{t("loesen.titel", { repo: repoLabel || t("loesen.repoFallback") })}</h2>
              </div>
              <button type="button" className="iio-modal-close" onClick={() => !unbinding && setConfirmUnbind(false)} aria-label={t("gemeinsam:aktion.schliessen")} disabled={unbinding}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="iio-modal-body">
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--iio-text-2)" }}>
                {t("loesen.text")}
              </p>
            </div>
            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left" />
              <div className="iio-modal-foot-right">
                <button type="button" className="iio-btn iio-btn--secondary" onClick={() => setConfirmUnbind(false)} disabled={unbinding}>{t("gemeinsam:aktion.abbrechen")}</button>
                {/* --warning, not --danger: the action is reversible and destroys nothing.
                    The repository, environments and instances stay, reconnecting is always
                    possible, as the dialog text above also says. Matches the opening
                    button .cp-linkbtn.warn. */}
                <button type="button" className="iio-btn iio-btn--warning" onClick={doUnbind} disabled={unbinding}>
                  {unbinding ? t("loesen.laeuft") : t("loesen.knopf")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rollback confirmation: switch from extended to SAP only. Native .iio-modal, not a MUI
          portal - a portal renders outside the .iio scope and loses the theme. */}
      {confirmRueckbau && (
        <div className="iio-modal-overlay is-open"
          onMouseDown={e => { if (e.target === e.currentTarget && !rueckbauBusy) setConfirmRueckbau(false); }}>
          <div className="iio-modal" role="dialog" aria-modal="true" aria-labelledby="cp-rueckbau-t"
            onMouseDown={e => e.stopPropagation()}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("modus.rueckbauEyebrow")}</span>
                <h2 id="cp-rueckbau-t">{t("modus.rueckbauTitel")}</h2>
              </div>
              <button type="button" className="iio-modal-close" onClick={() => !rueckbauBusy && setConfirmRueckbau(false)} aria-label={t("gemeinsam:aktion.schliessen")} disabled={rueckbauBusy}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="iio-modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--iio-text-2)" }}>
                {t("modus.rueckbauText")}
              </p>

              {updData?.erweiterung?.dateien?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauEntfernt")}</div>
                  <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12, color: "var(--iio-text-1)", lineHeight: 1.6 }}>
                    {updData.erweiterung.dateien.map(d => <li key={d} style={{ fontFamily: "var(--iio-font-mono)", fontSize: 12 }}>{d}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauBleibt")}</div>
                <p style={{ margin: 0, fontSize: 12.5, color: "var(--iio-text-2)", lineHeight: 1.5 }}>{t("modus.rueckbauBleibtText")}</p>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauVerliert")}</div>
                <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12.5, color: "var(--iio-text-2)", lineHeight: 1.7 }}>
                  <li>{t("modus.rueckbauVerliert1")}</li>
                  <li>{t("modus.rueckbauVerliert2")}</li>
                  <li>{t("modus.rueckbauVerliert3")}</li>
                </ul>
              </div>

              {rueckbauBlocker.length > 0 && (
                <div style={{ borderLeft: "2px solid var(--iio-warning)", paddingLeft: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6 }}>{t("modus.rueckbauLaufend")}</div>
                  <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: 12.5, lineHeight: 1.7 }}>
                    {rueckbauBlocker.map(l => (
                      <li key={l.id}>
                        <a href={l.html_url} target="_blank" rel="noreferrer" style={{ color: "var(--iio-link)" }}>{`#${l.id}`}</a>
                        {` \u00b7 ${l.status}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p style={{ margin: 0, fontSize: 12.5, color: "var(--iio-text-3)", lineHeight: 1.5 }}>
                {t("modus.rueckbauHinweis")}
              </p>
            </div>
            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left" />
              <div className="iio-modal-foot-right">
                <button type="button" className="iio-btn iio-btn--secondary" onClick={() => setConfirmRueckbau(false)} disabled={rueckbauBusy}>{t("gemeinsam:aktion.abbrechen")}</button>
                {/* --warning, not --danger: reversible via "reload", nothing is destroyed. */}
                <button type="button" className="iio-btn iio-btn--warning" onClick={doRueckbau} disabled={rueckbauBusy}>
                  {rueckbauBusy ? t("modus.rueckbauLaeuft") : (rueckbauBlocker.length ? t("modus.rueckbauErneut") : t("modus.rueckbauKnopf"))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}
